// AGC: MemberAgendaList - List of tasks assigned to a team member
// AGC: Shows tasks chronologically grouped by date
// AGC: Supports filtering by task status

import React, { useMemo } from 'react'
import type { Task } from '../../types/task'
import { Clock, MapPin, Calendar } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

interface MemberAgendaListProps {
  tasks: Task[]
  filter: 'pending' | 'completed' | 'all'
  onTaskClick: (task: Task) => void
}

/**
 * AGC: MemberAgendaList - Chronological task list for a member
 * AGC: Features:
 * AGC: - Groups tasks by date (today, tomorrow, this week, later)
 * AGC: - Shows task status, lot, and time window
 * AGC: - Filters by task status (pending/completed/all)
 * AGC: - Empty state when no tasks
 */
export const MemberAgendaList: React.FC<MemberAgendaListProps> = ({ tasks, filter, onTaskClick }) => {
  const { t } = useTranslation()
  
  // AGC: Filter tasks by status
  const filteredTasks = useMemo(() => {
    if (filter === 'pending') return tasks.filter(t => t.status !== 'completed')
    if (filter === 'completed') return tasks.filter(t => t.status === 'completed')
    return tasks
  }, [tasks, filter])
  
  // AGC: Sort tasks by planned start (or due date if no start)
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      const aDate = a.plannedStart || a.dueDate
      const bDate = b.plannedStart || b.dueDate
      return new Date(aDate).getTime() - new Date(bDate).getTime()
    })
  }, [filteredTasks])

  // AGC: Group tasks by time period
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const weekEnd = new Date(today)
  weekEnd.setDate(weekEnd.getDate() + 7)

  const todayTasks = sortedTasks.filter((t) => {
    const taskDate = new Date(t.plannedStart || t.dueDate)
    return taskDate >= today && taskDate < tomorrow
  })

  const tomorrowTasks = sortedTasks.filter((t) => {
    const taskDate = new Date(t.plannedStart || t.dueDate)
    const dayAfter = new Date(tomorrow)
    dayAfter.setDate(dayAfter.getDate() + 1)
    return taskDate >= tomorrow && taskDate < dayAfter
  })

  const thisWeekTasks = sortedTasks.filter((t) => {
    const taskDate = new Date(t.plannedStart || t.dueDate)
    const dayAfterTomorrow = new Date(tomorrow)
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1)
    return taskDate >= dayAfterTomorrow && taskDate < weekEnd
  })

  const laterTasks = sortedTasks.filter((t) => {
    const taskDate = new Date(t.plannedStart || t.dueDate)
    return taskDate >= weekEnd
  })

  // AGC: Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-500'
      case 'in-progress':
        return 'bg-blue-500'
      case 'overdue':
        return 'bg-red-500'
      default:
        return 'bg-yellow-500'
    }
  }

  // AGC: Get status label using i18n
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'completed':
        return t('tasks.statusCompleted')
      case 'in-progress':
        return t('tasks.statusInProgress')
      case 'overdue':
        return t('tasks.statusOverdue')
      default:
        return t('tasks.statusPending')
    }
  }

  // AGC: Format time window
  const formatTimeWindow = (task: Task): string => {
    if (!task.plannedStart || !task.plannedEnd) return ''
    
    const start = new Date(task.plannedStart)
    const end = new Date(task.plannedEnd)
    
    return `${start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
  }

  // AGC: Render task card
  const renderTaskCard = (task: Task) => (
    <button
      key={task.id}
      onClick={() => onTaskClick(task)}
      className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{task.title}</h4>
        <span
          className={`inline-block px-2 py-0.5 text-xs font-medium text-white ${getStatusColor(
            task.status
          )} rounded`}
        >
          {getStatusLabel(task.status)}
        </span>
      </div>

      <div className="space-y-1">
        {/* AGC: Lot name */}
        {task.plotName && (
          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <MapPin className="w-3 h-3" />
            <span>{task.plotName}</span>
          </div>
        )}

        {/* AGC: Time window */}
        {(task.plannedStart || task.plannedEnd) && (
          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <Clock className="w-3 h-3" />
            <span>{formatTimeWindow(task)}</span>
          </div>
        )}

        {/* AGC: Machine assigned */}
        {task.assignedMachineName && (
          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <span>🚜</span>
            <span>{task.assignedMachineName}</span>
          </div>
        )}
      </div>
    </button>
  )

  // AGC: Render section
  const renderSection = (title: string, tasks: Task[]) => {
    if (tasks.length === 0) return null

    return (
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{title}</h4>
        <div className="space-y-2">{tasks.map(renderTaskCard)}</div>
      </div>
    )
  }

  // AGC: Empty state
  if (sortedTasks.length === 0) {
    return (
      <div className="text-center py-8">
        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
        <p className="text-gray-600 dark:text-gray-400">
          {filter === 'pending' 
            ? (t('team.noPendingTasks') || 'Nenhuma tarefa pendente')
            : filter === 'completed'
            ? (t('team.noCompletedTasks') || 'Nenhuma tarefa concluída')
            : (t('team.noTasks') || 'Nenhuma tarefa')}
        </p>
      </div>
    )
  }

  return (
    <div>
      {renderSection('Hoje', todayTasks)}
      {renderSection('Amanhã', tomorrowTasks)}
      {renderSection('Esta Semana', thisWeekTasks)}
      {renderSection('Mais Tarde', laterTasks)}
    </div>
  )
}

export default MemberAgendaList
